import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

/**
 * Shared TableConnect back button.
 *
 * Used as the left-slot affordance of every page header (menu, cart,
 * checkout, bill, receipt, payment-result) so the back chrome is
 * byte-identical across surfaces. The visual contract was set by
 * the menu page (44×44 circular outline + perfectly centred SVG
 * chevron-left); see the comment in the menu template explaining
 * why the SVG variant is preferred over a typographic `‹` glyph
 * (the text glyph drifts above visual middle because of the
 * baseline).
 *
 * **Usage:**
 *
 * ```html
 * <tc-back-button ariaLabel="Back to menu" (back)="onBack()" />
 * ```
 *
 * The element is `display: contents` so its host disappears from
 * layout — the inner `<button>` participates directly in the
 * parent's grid/flex header, behaving exactly like the inline
 * `<button class="tc-back">` it replaces.
 *
 * `ViewEncapsulation.None` matches the convention set by
 * `TcFooterComponent` / `TcButtonComponent`: shared chrome whose
 * styles should never be silently undone by a parent's Emulated
 * scoping.
 */
@Component({
  selector: 'tc-back-button',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  template: `
    <button type="button"
            class="tc-back-button"
            [attr.aria-label]="ariaLabel"
            [attr.title]="title"
            (click)="back.emit($event)">
      <!-- SVG chevron-left — geometric path stays perfectly centred
           inside the circle, unlike the prior text chevron glyph
           which sat above the visual middle because of its
           typographic baseline. -->
      <svg width="20" height="20" viewBox="0 0 24 24"
           fill="none" stroke="currentColor" stroke-width="2.5"
           stroke-linecap="round" stroke-linejoin="round"
           aria-hidden="true">
        <path d="m15 18-6-6 6-6"/>
      </svg>
    </button>
  `,
  styles: [`
    /* display: contents lets the host element vanish from layout —
       the inner <button> sits directly in the parent's grid so the
       existing 44px 1fr 44px header columns keep working without
       any per-page wrapper tweaks. */
    tc-back-button { display: contents; }

    .tc-back-button {
      width: 44px; height: 44px; border-radius: 999px;
      display: grid; place-items: center;
      background: transparent; border: 1px solid var(--tc-border);
      color: var(--tc-fg); cursor: pointer;
      padding: 0;
      transition: background-color .15s;
    }
    .tc-back-button:active { background: var(--tc-bg-soft); }
    .tc-back-button:focus-visible {
      outline: 2px solid var(--tc-primary);
      outline-offset: 2px;
    }
  `],
})
export class TcBackButtonComponent {
  /** Accessible label. Defaults to the generic "Back" but every
   *  caller should override with a destination-aware string
   *  ("Back to menu", "Back to bill", etc.) — that's what screen
   *  readers announce. */
  @Input() ariaLabel = 'Back';

  /** Optional tooltip title. Most callers leave this null. */
  @Input() title: string | null = null;

  /** Click event. Named `back` rather than `click` to avoid
   *  shadowing the DOM click event on the host element. */
  @Output() back = new EventEmitter<MouseEvent>();
}
